import { createFileRoute, Link } from "@tanstack/react-router";
import { Book, HeadphonesIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_auth/app/_app/help/")({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();

	return (
		<div
			className="p-4 sm:p-6 max-w-4xl mx-auto"
			data-testid="help-main-content"
		>
			{/* Header */}
			<div className="mb-6 sm:mb-8" data-testid="help-header">
				<h1
					className="text-2xl sm:text-3xl font-bold text-base-content mb-2"
					data-testid="help-page-title"
				>
					{t("help.welcome")}
				</h1>
				<p
					className="text-base sm:text-lg text-base-content/70"
					data-testid="help-page-description"
				>
					{t("help.welcomeDescription")}
				</p>
			</div>

			{/* Mobile Navigation Cards */}
			<div className="md:hidden mb-6 grid grid-cols-1 gap-3">
				<Link
					to="/app/help/user-guide"
					className="flex items-center gap-3 p-4 bg-base-100 rounded-lg border border-base-300 hover:border-primary transition-colors"
				>
					<div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
						<Book className="w-5 h-5 text-primary" />
					</div>
					<div className="min-w-0">
						<h3 className="font-medium text-base-content">
							{t("help.userGuide")}
						</h3>
						<p className="text-sm text-base-content/70 truncate">
							{t("help.learnHowToUse")}
						</p>
					</div>
				</Link>
				<Link
					to="/app/help/support"
					className="flex items-center gap-3 p-4 bg-base-100 rounded-lg border border-base-300 hover:border-primary transition-colors"
				>
					<div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
						<HeadphonesIcon className="w-5 h-5 text-primary" />
					</div>
					<div className="min-w-0">
						<h3 className="font-medium text-base-content">
							{t("help.support")}
						</h3>
						<p className="text-sm text-base-content/70 truncate">
							{t("help.getHelp")}
						</p>
					</div>
				</Link>
			</div>

			{/* Getting Started Steps */}
			<div className="mb-8" data-testid="help-getting-started-section">
				<h2
					className="text-2xl font-semibold text-base-content mb-4"
					data-testid="help-getting-started-title"
				>
					{t("help.gettingStartedSteps")}
				</h2>
				<div
					className="bg-base-100 rounded-lg border border-base-300 p-6"
					data-testid="help-getting-started-container"
				>
					<ol className="space-y-4" data-testid="help-getting-started-steps">
						<li
							className="flex items-start space-x-3"
							data-testid="help-step-1"
						>
							<span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-medium">
								1
							</span>
							<div>
								<h3
									className="font-medium text-base-content"
									data-testid="help-step-1-title"
								>
									{t("help.step1Title")}
								</h3>
								<p
									className="text-base-content/70 text-sm"
									data-testid="help-step-1-description"
								>
									{t("help.step1Description")}
								</p>
							</div>
						</li>
						<li
							className="flex items-start space-x-3"
							data-testid="help-step-2"
						>
							<span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-medium">
								2
							</span>
							<div>
								<h3
									className="font-medium text-base-content"
									data-testid="help-step-2-title"
								>
									{t("help.step2Title")}
								</h3>
								<p
									className="text-base-content/70 text-sm"
									data-testid="help-step-2-description"
								>
									{t("help.step2Description")}
								</p>
							</div>
						</li>
						<li
							className="flex items-start space-x-3"
							data-testid="help-step-3"
						>
							<span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-medium">
								3
							</span>
							<div>
								<h3
									className="font-medium text-base-content"
									data-testid="help-step-3-title"
								>
									{t("help.step3Title")}
								</h3>
								<p
									className="text-base-content/70 text-sm"
									data-testid="help-step-3-description"
								>
									{t("help.step3Description")}
								</p>
							</div>
						</li>
						<li
							className="flex items-start space-x-3"
							data-testid="help-step-4"
						>
							<span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-medium">
								4
							</span>
							<div>
								<h3
									className="font-medium text-base-content"
									data-testid="help-step-4-title"
								>
									{t("help.step4Title")}
								</h3>
								<p
									className="text-base-content/70 text-sm"
									data-testid="help-step-4-description"
								>
									{t("help.step4Description")}
								</p>
							</div>
						</li>
						<li
							className="flex items-start space-x-3"
							data-testid="help-step-5"
						>
							<span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-medium">
								5
							</span>
							<div>
								<h3
									className="font-medium text-base-content"
									data-testid="help-step-5-title"
								>
									{t("help.step5Title")}
								</h3>
								<p
									className="text-base-content/70 text-sm"
									data-testid="help-step-5-description"
								>
									{t("help.step5Description")}
								</p>
							</div>
						</li>
						<li
							className="flex items-start space-x-3"
							data-testid="help-step-6"
						>
							<span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-medium">
								6
							</span>
							<div>
								<h3
									className="font-medium text-base-content"
									data-testid="help-step-6-title"
								>
									{t("help.step6Title")}
								</h3>
								<p
									className="text-base-content/70 text-sm"
									data-testid="help-step-6-description"
								>
									{t("help.step6Description")}
								</p>
							</div>
						</li>
					</ol>
				</div>
			</div>
		</div>
	);
}
